import { type ImportHeaderField, matchHeaders } from './matchHeaders';
import { papa } from './papaparse';

/** Separates the columns of the import API's own CSV format. */
export const IMPORT_COLUMN_SEPARATOR = '§';

export const IMPORT_DELIMITER_CANDIDATES = [
  IMPORT_COLUMN_SEPARATOR,
  ';',
  ',',
  '\t',
] as const;

/** Separates list items inside a cell, so it can never separate columns. */
const LIST_SEPARATOR_CHARACTER = '|';

export type SniffDelimiterOptions = {
  candidates?: readonly string[];
  /** Fields the headers are matched against, as {@link matchHeaders} does. */
  fields?: readonly ImportHeaderField[];
  requiredHeaders?: readonly string[];
};

function splitHeaderLine(text: string, delimiter: string): string[] {
  const { data } = papa.parse<string[]>(text, { delimiter, preview: 1 });
  return data[0] ?? [];
}

/**
 * Picks the column delimiter from a file's header line. With
 * `requiredHeaders`, the first candidate under which {@link matchHeaders}
 * finds all of them among `fields` wins;
 * otherwise, or when none does, the candidate yielding the most columns, ties
 * going to the earlier candidate.
 *
 * @returns `undefined` when no candidate splits the header line.
 */
export function sniffDelimiter(
  text: string,
  {
    candidates = IMPORT_DELIMITER_CANDIDATES,
    fields = [],
    requiredHeaders = [],
  }: SniffDelimiterOptions = {},
): string | undefined {
  const splits = candidates
    .filter((candidate) => candidate !== LIST_SEPARATOR_CHARACTER)
    .map((delimiter) => ({
      delimiter,
      headers: splitHeaderLine(text, delimiter),
    }))
    .filter(({ headers }) => headers.length > 1);

  const knownFields = [
    ...fields,
    ...requiredHeaders
      .filter((header) => !fields.some(({ field }) => field === header))
      .map((field) => ({ field })),
  ];
  const withRequiredHeaders = splits.find(({ headers }) => {
    const matched = new Set(
      Object.values(matchHeaders(headers, knownFields).mapping),
    );
    return requiredHeaders.every((header) => matched.has(header));
  });
  if (requiredHeaders.length > 0 && withRequiredHeaders) {
    return withRequiredHeaders.delimiter;
  }

  const widest = splits.reduce<(typeof splits)[number] | undefined>(
    (best, split) =>
      best && best.headers.length >= split.headers.length ? best : split,
    undefined,
  );
  return widest?.delimiter;
}
